import { type StudyStepSlug, isStudyStepSlug } from '@sigora/domain';

export type Route =
  | { name: 'studies' }
  | { name: 'study'; studyId: string }
  | { name: 'study-step'; studyId: string; stepSlug: StudyStepSlug }
  | { name: 'account' }
  | SignedOutRoute;

/**
 * The screens that render whether or not a session exists: the ones a signed-out visitor needs to
 * get an account or back into one, and the two a mailed link opens — which a browser already
 * signed in, perhaps as somebody else, can follow too.
 */
export type SignedOutRoute =
  | { name: 'signup' }
  | { name: 'verify-email' }
  | { name: 'forgot-password' }
  | { name: 'reset-password' };

const SIGNED_OUT_ROUTE_NAMES = new Set<string>([
  'signup',
  'verify-email',
  'forgot-password',
  'reset-password',
]);

export const isSignedOutRoute = (route: Route): route is SignedOutRoute =>
  SIGNED_OUT_ROUTE_NAMES.has(route.name);

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The address bar, read as one of the app's screens.
 *
 * A step is the addressable unit of a study, and `/studies/<id>` with no step is the study asking
 * to be sent to whichever step it remembers — which only the server knows, so it resolves to a
 * screen that reads the study and replaces its own URL.
 *
 * An unknown path is the study list rather than a 404: the list is the answer to anything a user
 * could be looking for. A `/studies/<not an id>` path resolves there too, for the same reason — a
 * mistyped or stale link should land somewhere usable. A study id followed by something that is
 * not a step is the study itself, which is the nearest thing that does exist.
 *
 * The account screens are single segments and nothing else: `/signup/extra` names no screen, so it
 * is the list like any other unknown path.
 */
export const parseRoute = (pathname: string): Route => {
  const segments = pathname.split('/').filter((segment) => segment !== '');

  if (segments.length === 1) {
    const [only] = segments;

    if (only === 'account') {
      return { name: 'account' };
    }

    if (only !== undefined && SIGNED_OUT_ROUTE_NAMES.has(only)) {
      return { name: only as SignedOutRoute['name'] };
    }
  }

  if (segments[0] === 'studies') {
    const studyId = segments[1];
    if (studyId !== undefined && UUID.test(studyId)) {
      const stepSlug = segments[2];

      return stepSlug !== undefined && isStudyStepSlug(stepSlug)
        ? { name: 'study-step', studyId, stepSlug }
        : { name: 'study', studyId };
    }
  }

  return { name: 'studies' };
};

export const routeToPath = (route: Route): string => {
  switch (route.name) {
    case 'study-step':
      return `/studies/${route.studyId}/${route.stepSlug}`;
    case 'study':
      return `/studies/${route.studyId}`;
    case 'account':
    case 'signup':
    case 'verify-email':
    case 'forgot-password':
    case 'reset-password':
      return `/${route.name}`;
    default:
      return '/';
  }
};

const TOKEN = /^[A-Za-z0-9_-]+$/;

/**
 * The token a mailed link carries, read out of the URL fragment (`#token=…`), or null when there is
 * none or it is not the shape a token has.
 *
 * The fragment is where the link puts it because a fragment never leaves the browser: no request
 * line, server log or `Referer` sees it. A malformed one is null rather than sent, since the
 * server's answer to it is the same as to an expired one and the screen can give that answer
 * without asking.
 */
export const readFragmentToken = (hash: string): string | null => {
  const token = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash).get('token');

  return token !== null && TOKEN.test(token) ? token : null;
};
